import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Badge,
  Banner,
  Breadcrumbs,
  Button,
  Empty,
  LayerCard,
  Loader,
  Table,
  useKumoToastManager,
} from '@cloudflare/kumo'
import {
  HouseIcon,
  PencilSimpleIcon,
  PlusIcon,
  PrinterIcon,
  TrashIcon,
} from '@phosphor-icons/react'
import { ApiError } from '../api/client'
import { useHasRole } from '../auth/useRoles'
import { deletePrinter, listPrinters, type PrinterDto, type PrinterStatus } from '../api/printers'
import { PrinterStatusBadge } from '../components/PrinterStatusBadge'
import { PrinterConnectionBadge } from '../components/PrinterConnectionBadge'
import { DeleteResource } from '../components/kumo/delete-resource/delete-resource'
import { PageHeader } from '../components/kumo/page-header/page-header'
import { useTranslation } from '../i18n'
import { apiRequest } from '../api/client'
import type { Building } from '@hub-wifi/shared'

/* ------------------------------------------------------------------ */
/*  PrintersListPage                                                   */
/*                                                                     */
/*  Lists all printers across the account. Building names are resolved */
/*  client-side from the existing /api/buildings response so the table */
/*  shows a human label instead of the raw ULID.                       */
/* ------------------------------------------------------------------ */

type PrinterTab = 'all' | 'active' | 'pending' | 'inactive'

/** Groups the five PrinterStatus values into the three filterable tabs. */
function tabForStatus(status: PrinterStatus): Exclude<PrinterTab, 'all'> {
  if (status === 'active') return 'active'
  if (status === 'pending_oauth' || status === 'pending_verification') return 'pending'
  return 'inactive' // 'inactive' | 'revoked'
}

// Delete requires the printer to already be deactivated — mirrors
// printerService.del's PRINTER_NOT_DELETABLE gate (same predicate as the
// merged printer detail page, apps/admin/src/pages/PrinterDetailPage.tsx).
const canDelete = (s: PrinterStatus) => s === 'inactive'

export function PrintersListPage() {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toasts = useKumoToastManager()

  const [printers, setPrinters] = useState<PrinterDto[] | null>(null)
  const [buildings, setBuildings] = useState<Map<string, string>>(new Map())
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<PrinterTab>('all')

  // Delete confirmation (typed-name) dialog state.
  const [printerToDelete, setPrinterToDelete] = useState<PrinterDto | null>(null)

  // Printer creation and deletion stay admin-only; operators manage and
  // monitor existing printers. Hide the controls they cannot use.
  const isAdmin = useHasRole('admin')
  const [isDeleting, setIsDeleting] = useState(false)

  const load = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      const [list, buildingList] = await Promise.all([
        listPrinters(token),
        apiRequest<Building[]>('/api/buildings', { token }),
      ])
      setPrinters(list)
      setBuildings(new Map(buildingList.map((b) => [b.id, b.name])))
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
    } finally {
      setLoading(false)
    }
  }, [getAccessTokenSilently])

  useEffect(() => {
    void load()
  }, [load])

  async function handleDeletePrinter(printer: PrinterDto) {
    try {
      setIsDeleting(true)
      const token = await getAccessTokenSilently()
      await deletePrinter(token, printer.id)
      toasts.add({ variant: 'success', title: t('printers.deleteSuccess') })
      setPrinterToDelete(null)
      setLoading(true)
      await load()
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const filteredPrinters =
    activeTab === 'all'
      ? (printers ?? [])
      : (printers ?? []).filter((p) => tabForStatus(p.status) === activeTab)

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('printers.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('printers.title')}
        description={t('printers.description')}
        tabs={[
          { label: t('printers.tabs.all'), value: 'all' },
          { label: t('printers.tabs.active'), value: 'active' },
          { label: t('printers.tabs.pending'), value: 'pending' },
          { label: t('printers.tabs.inactive'), value: 'inactive' },
        ]}
        defaultTab={activeTab}
        onValueChange={(value) => setActiveTab(value as PrinterTab)}
      >
        {isAdmin && (
          <Button
            variant="primary"
            size="sm"
            icon={<PlusIcon size={16} />}
            onClick={() => navigate('/admin/printers/new')}
          >
            {t('printers.addPrinter')}
          </Button>
        )}
      </PageHeader>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error === 'GENERIC_ERROR' ? t('common.error') : error}
        </Banner>
      )}

      {!loading && !error && printers && printers.length === 0 && (
        <Empty
          icon={<PrinterIcon size={48} className="text-kumo-inactive" />}
          title={t('printers.empty')}
        />
      )}

      {!loading && !error && printers && printers.length > 0 && filteredPrinters.length === 0 && (
        <Empty
          icon={<PrinterIcon size={48} className="text-kumo-inactive" />}
          title={t('common.noResultsForFilter')}
        />
      )}

      {!loading && !error && filteredPrinters.length > 0 && (
        <LayerCard>
          <LayerCard.Primary className="p-0">
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.Head>{t('printers.name')}</Table.Head>
                    <Table.Head>{t('printers.building')}</Table.Head>
                    <Table.Head>{t('printers.statusLabel')}</Table.Head>
                    <Table.Head>{t('printers.verifiedAt')}</Table.Head>
                    <Table.Head>{t('printers.lastTest')}</Table.Head>
                    <Table.Head>{t('printers.actions')}</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredPrinters.map((p) => (
                    <Table.Row key={p.id}>
                      <Table.Cell>
                        <button
                          type="button"
                          className="text-sm font-medium text-kumo-default hover:text-kumo-link cursor-pointer bg-transparent border-none"
                          onClick={() => navigate(`/admin/printers/${p.id}`)}
                        >
                          {p.name}
                        </button>
                      </Table.Cell>
                      <Table.Cell>
                        {buildings.get(p.buildingId) ?? (
                          <Badge variant="neutral">{p.buildingId.slice(0, 8)}…</Badge>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex flex-wrap gap-1">
                          <PrinterStatusBadge status={p.status} />
                          {p.status === 'active' && (
                            <PrinterConnectionBadge
                              connected={p.epsonConnected}
                              checkedAt={p.connectionCheckedAt}
                            />
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        {p.verifiedAt
                          ? new Date(p.verifiedAt).toLocaleDateString()
                          : t('printers.never')}
                      </Table.Cell>
                      <Table.Cell>
                        {p.lastTestPrintAttemptAt
                          ? new Date(p.lastTestPrintAttemptAt).toLocaleString()
                          : t('printers.never')}
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<PencilSimpleIcon size={16} />}
                            onClick={() => navigate(`/admin/printers/${p.id}`)}
                          >
                            {t('common.edit')}
                          </Button>
                          {canDelete(p.status) && isAdmin && (
                            <Button
                              variant="secondary-destructive"
                              size="sm"
                              icon={<TrashIcon size={16} />}
                              onClick={() => setPrinterToDelete(p)}
                            >
                              {t('printers.detail.delete')}
                            </Button>
                          )}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </LayerCard.Primary>
        </LayerCard>
      )}

      <DeleteResource
        open={!!printerToDelete}
        onOpenChange={(next) => {
          if (!next) setPrinterToDelete(null)
        }}
        resourceType={t('deleteResource.type.printer')}
        resourceName={printerToDelete?.name ?? ''}
        isDeleting={isDeleting}
        onDelete={() => (printerToDelete ? handleDeletePrinter(printerToDelete) : undefined)}
      />
    </div>
  )
}
